import { useEffect, useState } from 'react';
import { Dashboard } from './components/Dashboard';
import { Navigation } from './components/Navigation';
import { RefLinks } from './components/RefLinks';
import { Statistics } from './components/Statistics';
import { Referrals } from './components/Referrals';
import { Withdrawals } from './components/Withdrawals';
import { useTelegram } from './hooks/useTelegram';
import { usePartnerData } from './hooks/usePartnerData';
import './App.css';

type Page = 'dashboard' | 'statistics' | 'referrals' | 'withdrawals';

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [refLinkFilter, setRefLinkFilter] = useState<string | undefined>(undefined);
  const { user, initDataRaw, isReady, isInTelegram } = useTelegram();
  const { profile, statistics, referrals, isLoading, error } = usePartnerData(initDataRaw, refLinkFilter);

  // Блокировка доступа не из Telegram (только в production)
  if (!isInTelegram && !import.meta.env.DEV && isReady) {
    return (
      <div className="error-screen">
        <div className="error-icon" style={{ fontSize: '4rem' }}>🔒</div>
        <h2>Доступ запрещен</h2>
        <p>Это приложение доступно только через Telegram Mini App</p>
        <div style={{ marginTop: '2rem', fontSize: '0.9rem', opacity: 0.7 }}>
          Откройте приложение из Telegram бота
        </div>
      </div>
    );
  }

  useEffect(() => {
    // Применяем тему Telegram
    const tg = window.Telegram?.WebApp;
    if (tg) {
      document.documentElement.style.setProperty('--tg-theme-bg-color', tg.backgroundColor);
      document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#000000');
    }
  }, []);

  if (!isReady) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen">
        <div className="error-icon">⚠️</div>
        <h2>Ошибка загрузки</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>WinTon Partners</h1>
          {user && (
            <div className="user-info">
              <span className="user-name">{user.first_name}</span>
              {profile && (
                <span className="user-level">{profile.level}</span>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="app-main">
        {isLoading ? (
          <div className="loading-container">
            <div className="loader"></div>
          </div>
        ) : (
          <>
            {currentPage === 'dashboard' && (
              <>
                {statistics?.refLinks && statistics.refLinks.length > 0 && (
                  <RefLinks refLinks={statistics.refLinks} />
                )}
                <Dashboard profile={profile} statistics={statistics} />
              </>
            )}
            {currentPage === 'statistics' && (
              <Statistics 
                statistics={statistics} 
                refLinkFilter={refLinkFilter}
                onRefLinkChange={setRefLinkFilter}
              />
            )}
            {currentPage === 'referrals' && (
              <Referrals referrals={referrals} />
            )}
            {currentPage === 'withdrawals' && (
              <Withdrawals profile={profile} />
            )}
          </>
        )}
      </main>

      <Navigation currentPage={currentPage} onNavigate={setCurrentPage} />
    </div>
  );
}

export default App;
